import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Cascader, DatePicker, Input, InputNumber, Modal, Select } from 'antd'
import dayjs from '@plugins/local-dayjs'

import { getExamDictData, getRegionTree } from '../../../../common/request'
import ResponseCode from '../../../../common/response-code'
import { updateDictInfo } from '../../../../store/slice/dict-slice'
import { FieldTypeMap } from '../../../../common/common'
import { isNumericCompareLogic, isEmptyCheckLogic } from './applyFieldRuleOptions'

const toDayjs = (date) => {
  if (date == null || date === '') {
    return null
  }
  if (typeof date?.isValid === 'function') {
    return date.isValid() ? date : null
  }
  const parsed = dayjs(date)
  return parsed.isValid() ? parsed : null
}

const normalizeDateRangeValue = (val) => {
  if (val == null || val === '') {
    return null
  }

  if (!Array.isArray(val)) {
    const parsed = toDayjs(val)
    return parsed ? [parsed, parsed] : null
  }

  if (!val.length) {
    return null
  }

  const normalized = val.map(toDayjs)
  if (normalized.every(item => item == null)) {
    return null
  }

  return [
    normalized[0] ?? null,
    normalized[1] ?? null
  ]
}

const ApplyFieldRuleContent = forwardRef(({ ruleItem, contentLogicType, disabled, value, onChange }, ref) => {
  const dispatch = useDispatch()
  const dictInfo = useSelector(state => state.dict.dictInfo)
  const provinceList = useSelector(state => {
    return state.regionList.regionList.map(item => {
      return {
        label: item.name,
        value: item.code,
        id: item.id,
        isLeaf: false
      }
    })
  })

  const [options, setOptions] = useState([])
  const [regionOptions, setRegionOptions] = useState(provinceList)

  useImperativeHandle(ref, () => {
    return {
      getRegionTreeAction
    }
  })

  useEffect(() => {
    if (ruleItem?.fieldType === FieldTypeMap.select) {
      if (ruleItem.fieldJoinSelect && !dictInfo[ruleItem.fieldJoinSelect]) {
        loadSelectOptions(ruleItem.fieldJoinSelect)
      } else if (ruleItem.fieldJoinSelect) {
        setSelectOptions(dictInfo[ruleItem.fieldJoinSelect])
      }
    }
  }, [ruleItem])

  const loadSelectOptions = (dictCode) => {
    getExamDictData({ dictCode }).then(res => {
      if (res.code === ResponseCode.success && res.data) {
        dispatch(updateDictInfo({ dictKey: dictCode, dictValue: res.data }))
        setSelectOptions(res.data)
      } else {
        Modal.error({
          title: '错误',
          content: res.msg,
          centered: true
        })
      }
    }).catch(() => {
      Modal.error({
        title: '网络错误',
        content: '查询字典数据失败',
        centered: true
      })
    })
  }

  const setSelectOptions = (dictList) => {
    setOptions((dictList || []).map(dict => {
      return {
        label: dict.dictLabel,
        value: dict.dictLabel,
        dict
      }
    }))
  }

  const loadRegionData = (selectedOptions) => {
    const targetOption = selectedOptions[selectedOptions.length - 1]
    targetOption.loading = true

    if (targetOption.childList) {
      targetOption.loading = false
      targetOption.children = targetOption.childList.map(item => {
        return {
          label: item.name,
          value: item.code,
          id: item.id,
          isLeaf: !item.childList,
          childList: item.childList
        }
      })
      setRegionOptions([...regionOptions])
    } else {
      getRegionTreeAction(targetOption)
    }
  }

  const getRegionTreeAction = (targetOption) => {
    getRegionTree({ parent: targetOption.value }).then(res => {
      if (res.code === ResponseCode.success && res.data) {
        targetOption.loading = false
        targetOption.children = res.data.map(item => {
          return {
            label: item.name,
            value: item.code,
            id: item.id,
            isLeaf: !item.childList,
            childList: item.childList
          }
        })
        setRegionOptions([...regionOptions])
      } else {
        Modal.error({
          title: '错误',
          content: res.msg,
          centered: true
        })
      }
    }).catch(() => {
      targetOption.loading = false
      Modal.error({
        title: '网络错误',
        content: '查询区域失败',
        centered: true
      })
    })
  }

  const renderNumberInput = () => {
    return (
      <InputNumber
        disabled={ disabled }
        value={ value === '' || value == null ? null : Number(value) }
        onChange={ (num) => onChange(num == null ? undefined : num) }
        style={ { width: 200 } }
        controls
      ></InputNumber>
    )
  }

  const renderContent = () => {
    if (isEmptyCheckLogic(contentLogicType)) {
      return (
        <Input
          disabled
          placeholder=""
          style={ { width: 200 } }
        ></Input>
      )
    }

    if (isNumericCompareLogic(contentLogicType)) {
      return renderNumberInput()
    }

    switch (ruleItem?.fieldType) {
      case FieldTypeMap.input:
        return (
          <Input.TextArea
            disabled={ disabled }
            value={ value }
            onChange={ onChange }
            autoSize={ true }
            style={ { width: 350 } }
          ></Input.TextArea>
        )
      case FieldTypeMap.number:
        return renderNumberInput()
      case FieldTypeMap.select:
        return (
          <Select
            disabled={ disabled }
            mode="multiple"
            value={ value }
            options={ options }
            onChange={ (selectedValue, option) => onChange(selectedValue, option) }
            style={ { width: 350 } }
          ></Select>
        )
      case FieldTypeMap.dateRangePicker:
        return (
          <DatePicker.RangePicker
            disabled={ disabled }
            value={ normalizeDateRangeValue(value) }
            onChange={ onChange }
            style={ { width: 300 } }
            format="YYYY-MM-DD"
            allowEmpty
          ></DatePicker.RangePicker>
        )
      case FieldTypeMap.cascader:
        return (
          <Cascader
            disabled={ disabled }
            changeOnSelect
            value={ value }
            options={ regionOptions }
            loadData={ loadRegionData }
            placeholder="请选择区域"
            style={ { width: '100%' } }
            onChange={ onChange }
          ></Cascader>
        )
      default:
        return <Input disabled={ disabled } value={ value } onChange={ onChange }></Input>
    }
  }

  return (
    <div ref={ ref }>
      { renderContent() }
    </div>
  )
})

export default ApplyFieldRuleContent
